import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ms from "ms";
import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import useMyService from "../service/useMyService";

function buildEntityQueryKey(queryKeyLabel, queryKeyId) {
  const key = [queryKeyLabel];
  if (queryKeyId != null) {
    key.push(queryKeyId);
  }
  return key;
}

function parseStaleTime(staleTime) {
  if (staleTime === Infinity || typeof staleTime === "number") {
    return staleTime;
  }
  return ms(staleTime);
}

/**
 * User-visible detail for failed entity requests (axios + `unwrapMarketplaceBody` errors).
 * @param {unknown} error
 * @returns {string}
 */
function getEntityOperationErrorDetail(error) {
  if (!error) return "Request failed";

  const normalizedMsg =
    typeof error === "object" &&
    error !== null &&
    "normalizedApiError" in error &&
    error.normalizedApiError &&
    typeof error.normalizedApiError.message === "string"
      ? error.normalizedApiError.message.trim()
      : "";

  let message =
    normalizedMsg ||
    (error instanceof Error ? error.message.trim() : "") ||
    (typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
      ? error.message.trim()
      : "");

  const code =
    typeof error === "object" && error !== null && "apiError" in error
      ? error.apiError &&
        typeof error.apiError === "object" &&
        error.apiError !== null &&
        "code" in error.apiError
        ? String(error.apiError.code)
        : ""
      : typeof error === "object" &&
          error !== null &&
          "normalizedApiError" in error &&
          error.normalizedApiError &&
          typeof error.normalizedApiError.code === "string"
        ? error.normalizedApiError.code
        : "";

  if (!message) message = "Request failed";
  if (code && code !== "API_ERROR" && !message.includes(code)) {
    return `${message} (${code})`;
  }
  return message;
}

function invalidateEntityQueries({
  queryClient,
  queryKey,
  invalidateQueriesList,
}) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey }),
    ...invalidateQueriesList.map((invalidatedQueryKey) =>
      queryClient.invalidateQueries({ queryKey: [...invalidatedQueryKey] })
    ),
  ]);
}

function getEntityMutationFn({
  addEntity,
  updateEntity,
  deleteEntity,
  endpoint,
  mutationType,
}) {
  switch (mutationType) {
    case "ADD":
      return ({ dataObj, signal }) =>
        addEntity({
          dataObj,
          endpoint,
          signal,
        });
    case "UPDATE":
      return ({ dataObj, signal }) =>
        updateEntity({
          dataObj,
          endpoint,
          signal,
        });
    case "DELETE":
      return ({ id, signal }) =>
        deleteEntity({
          id,
          endpoint,
          signal,
        });
    default:
      return () => undefined;
  }
}

export function useQueryEntities({ apiConfig }) {
  const {
    endpoint = "",
    queryKeyLabel = "",
    queryKeyId = null,
    params = {},
    enabledParam = true,
    retry = 2,
    staleTime = "1h",
  } = apiConfig;

  const { getAllEntities } = useMyService();

  const queryKey = useMemo(
    () => buildEntityQueryKey(queryKeyLabel, queryKeyId),
    [queryKeyLabel, queryKeyId]
  );

  const { data, error, isError, isSuccess, isLoading } = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      getAllEntities({
        params,
        endpoint,
        signal,
      }),
    enabled: !!enabledParam,
    retry: retry !== 2 ? retry : 2,
    staleTime: parseStaleTime(staleTime),
  });

  // Removed problematic useEffect that was invalidating queries on success,
  // which caused the query to run twice. Queries should only be invalidated
  // after mutations, not automatically on every successful fetch.

  useEffect(() => {
    if (!error) return;
    const detail = getEntityOperationErrorDetail(error);
    toast.error(
      queryKeyLabel
        ? `Could not load ${queryKeyLabel}: ${detail}`
        : `Could not load data: ${detail}`
    );
  }, [error, queryKeyLabel]);

  return {
    data,
    isLoading,
    isError,
    error,
    isSuccess,
  };
}

export function useQueryEntityById({ apiConfig }) {
  const {
    endpoint = "",
    queryKeyLabel = "",
    queryKeyId = "",
    enabledParam = null,
    retry = 2,
    staleTime = "0h",
    initialData = undefined,
    cacheTime = 0,
    refetchOnWindowFocus,
  } = apiConfig;

  const { getEntityById } = useMyService();

  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => buildEntityQueryKey(queryKeyLabel, queryKeyId),
    [queryKeyLabel, queryKeyId]
  );

  const { data, error, isError, isLoading, isSuccess } = useQuery({
    queryKey,
    queryFn: async ({ signal }) =>
      getEntityById({ id: queryKeyId, endpoint, signal }),
    enabled: !!enabledParam,
    retry: retry !== 2 ? retry : 2,
    staleTime: parseStaleTime(staleTime),
    initialData,
    ...(refetchOnWindowFocus !== undefined ? { refetchOnWindowFocus } : {}),
    gcTime: cacheTime,
  });

  useEffect(() => {
    if (!error) return;
    const detail = getEntityOperationErrorDetail(error);
    toast.error(
      queryKeyLabel
        ? `Could not load ${queryKeyLabel}: ${detail}`
        : `Could not load data: ${detail}`
    );
  }, [error, queryKeyLabel]);

  useEffect(() => {
    if (initialData) {
      queryClient.invalidateQueries({ queryKey: queryKey });
    }
  }, [initialData, queryClient, queryKey]);

  return {
    data,
    isLoading,
    isError,
    isSuccess,
  };
}

export function useMutateEntity({ apiConfig }) {
  const {
    endpoint = "",
    queryKeyLabel = "",
    queryKeyId = "",
    retry = 2,
    invalidateQueriesList = [],
    mutationType = "",
  } = apiConfig;

  const { addEntity, updateEntity, deleteEntity } = useMyService();

  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => buildEntityQueryKey(queryKeyLabel, queryKeyId),
    [queryKeyLabel, queryKeyId]
  );
  const mutationFn = useMemo(
    () =>
      getEntityMutationFn({
        addEntity,
        updateEntity,
        deleteEntity,
        endpoint,
        mutationType,
      }),
    [addEntity, updateEntity, deleteEntity, endpoint, mutationType]
  );

  const { isSuccess, data, mutateAsync, isError, error, isPending } =
    useMutation({
      mutationFn,
      retry,
      onSuccess: async () => {
        await invalidateEntityQueries({
          queryClient,
          queryKey,
          invalidateQueriesList,
        });
        toast.success("Operation successful.");
      },
    });

  useEffect(() => {
    if (!error) return;
    const detail = getEntityOperationErrorDetail(error);
    toast.error(
      queryKeyLabel
        ? `Could not save ${queryKeyLabel}: ${detail}`
        : `Could not save: ${detail}`
    );
  }, [error, queryKeyLabel]);

  return {
    data,
    mutateAsync,
    isError,
    isPending,
  };
}
